import { useCallback } from "react";
import { toast } from "sonner";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useIssuesStore } from "@/store/zustand/issues-store";
import { useDesignCommandManager } from "@/store/zustand/designCommandManagerStore";
import { CreateStoryChange } from "@/store/zustand/changeLedgerTypes";
import { useCanvasId, useActiveProject } from "@/utils/utils";
import { findExistingStoryFile } from "./utils";
import type { Instance } from "./instancesStore";
import type { JsxElement } from "@/utils/jsx/jsxElementTypes";

/**
 * Hook that provides a function to save an instance as a story variant.
 *
 * @param selectedAsset - The component definition that instances belong to
 * @returns saveInstanceAsStory - Function to save an instance as a story
 */
export function useSaveInstanceAsStory(selectedAsset: JsxElement) {
  const canvasId = useCanvasId();
  const project = useActiveProject();

  const treeElementLookup = useCodebaseStore(
    (state) => state.treeElementLookup,
  );

  const storyboards = useIssuesStore(
    (state) => state.currentCanvas[canvasId || ""]?.storyboards || [],
  );

  const saveInstanceAsStory = useCallback(
    async (instance: Instance) => {
      if (
        !canvasId ||
        !instance.dragData.fileContents ||
        !instance.dragData.fileContentsSourceFilename
      ) {
        toast.error("Cannot save as story: missing required data");
        return;
      }

      // Find existing story file for this component
      const existingStoryFile = findExistingStoryFile(
        { framework: project?.framework },
        treeElementLookup,
        selectedAsset,
      );

      // Get the storyboard display name (same logic as useStoryboardName)
      const storyboard = storyboards.find(
        (sb) => sb.id === instance.storyboardId,
      );
      const storyName =
        storyboard?.name ||
        storyboard?.storyComponentName ||
        storyboard?.route ||
        "Instance";

      // Create the change using the ChangeLedger pattern
      const change = new CreateStoryChange({
        storyFilePath: existingStoryFile,
        fileContents: instance.dragData.fileContents,
        fileContentsSourceFilename:
          instance.dragData.fileContentsSourceFilename,
        storyName,
        componentName: instance.componentName,
        componentFilepath: instance.componentFilepath,
        isDefaultExport: selectedAsset.defaultExport,
      });

      try {
        await useDesignCommandManager
          .getState()
          .executeCommandAsPromise(change);
        // Toast is shown automatically via successToastMessage in CreateStoryChange
      } catch (error) {
        console.error(
          "[useSaveInstanceAsStory] Failed to save as story:",
          error,
        );
        toast.error(
          `Failed to save as story: ${error instanceof Error ? error.message : "Unknown error"}`,
        );
      }
    },
    [
      canvasId,
      project?.framework,
      treeElementLookup,
      selectedAsset,
      storyboards,
    ],
  );

  return { saveInstanceAsStory };
}
